// A4Canvas.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTable } from "../../context/TableContext.js";
import EditableTable from "./EditableTable.js";

export default function A4Canvas() {
  const { state } = useTable();

  // ---- Zoom (1.0 = 100%)
  const [zoom, setZoom] = useState(0.5);
  const containerRef = useRef(null);

  // ---- Base paper sizes (landscape bases, in px @300dpi-ish)
  const paperBase = useMemo(() => {
    switch (state.paperType) {
      case "a4":     return { w: 3508, h: 2480 };
      case "a3":     return { w: 4961, h: 3508 };
      case "letter": return { w: 3300, h: 2550 };
      default:       return { w: 3508, h: 2480 };
    }
  }, [state.paperType]);

  // ---- Apply orientation
  const paper = useMemo(
    () => (state.orientation === "portrait" ? { w: paperBase.h, h: paperBase.w } : paperBase),
    [paperBase, state.orientation]
  );

  // ---- Helpers
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const setZoomClamped = (z) => setZoom(clamp(z, 0.1, 4)); // 10%–400%

  const zoomIn = () => setZoomClamped(zoom * 1.1);
  const zoomOut = () => setZoomClamped(zoom / 1.1);
  const reset100 = () => setZoomClamped(1);

  // ---- Keyboard zoom: Ctrl/Cmd + '+', '-', '0'
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key === "=" || e.key === "+") { e.preventDefault(); zoomIn(); }
      else if (e.key === "-")             { e.preventDefault(); zoomOut(); }
      else if (e.key === "0")             { e.preventDefault(); reset100(); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [zoom]);

  // ---- Mouse wheel zoom when holding Ctrl/Cmd
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      e.deltaY > 0 ? zoomOut() : zoomIn();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoom]);

  // ---- Fit to width
  const fitToWidth = () => {
    const el = containerRef.current;
    if (!el) return;
    const available = el.clientWidth - 32; // small padding allowance
    const scale = available > 0 ? available / paper.w : 1;
    setZoomClamped(scale);
  };

  // Fit once on mount
  useEffect(() => { fitToWidth(); }, []); // eslint-disable-line

  // Refit on paper/orientation changes
  useEffect(() => { fitToWidth(); }, [paper.w, paper.h]); // eslint-disable-line

  // Refit when container resizes
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => fitToWidth());
    ro.observe(el);
    return () => ro.disconnect();
  }, []); // eslint-disable-line

  return (
    <main ref={containerRef} className="flex-1 bg-white overflow-auto p-4 min-h-0">
      {/* Scoped CSS: hide LAST COLUMN by forcing its width to 0 (no display:none/hidden) */}
      <style>{`
        .rn-hide-last-col table {
          table-layout: fixed;        /* widths are respected */
          border-collapse: separate;
          border-spacing: 0;
        }
        /* If colgroup exists, zero last col there */
        .rn-hide-last-col table colgroup col:last-child {
          width: 0 !important;
        }
        /* Zero width for last TH/TD in every row */
        .rn-hide-last-col table tr > *:last-child {
          inline-size: 0 !important;
          width: 0 !important;
          min-inline-size: 0 !important;
          min-width: 0 !important;
          max-inline-size: 0 !important;
          max-width: 0 !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
          border-left-width: 0 !important;
          border-right-width: 0 !important;
          overflow: hidden !important;
          white-space: nowrap;
        }
        /* Prevent inner wrappers forcing expansion */
        .rn-hide-last-col table tr > *:last-child > * {
          inline-size: 0 !important;
          width: 0 !important;
          max-inline-size: 0 !important;
          max-width: 0 !important;
          overflow: hidden !important;
        }
      `}</style>

      {/* Zoom toolbar */}
      <div className="sticky top-0 z-10 mb-3 flex items-center gap-2 bg-white/80 backdrop-blur px-3 py-2 rounded-md border border-EAECF0 w-fit shadow-sm">
        <button
          onClick={zoomOut}
          type="button"
          className="flex items-center justify-center w-8 h-8 rounded border border-EAECF0 hover:bg-f3f8ff"
          title="Zoom out (Ctrl/Cmd + -)"
          aria-label="Zoom out"
        >
          <span className="text-lg">−</span>
        </button>

        <div className="min-w-[64px] text-center text-sm font-medium tabular-nums">
          {(zoom * 100).toFixed(0)}%
        </div>

        <button
          onClick={zoomIn}
          type="button"
          className="flex items-center justify-center w-8 h-8 rounded border border-EAECF0 hover:bg-f3f8ff"
          title="Zoom in (Ctrl/Cmd + +)"
          aria-label="Zoom in"
        >
          <span className="text-lg">+</span>
        </button>

        <div className="mx-2 h-5 w-px bg-EAECF0" />

        <button
          onClick={reset100}
          type="button"
          className="px-2 py-1 rounded border border-EAECF0 hover:bg-f3f8ff text-sm"
          title="Reset to 100% (Ctrl/Cmd + 0)"
        >
          100%
        </button>

        <button
          onClick={fitToWidth}
          type="button"
          className="px-2 py-1 rounded border border-EAECF0 hover:bg-f3f8ff text-sm"
          title="Fit to width"
        >
          Fit
        </button>
      </div>

      {/* Scaled page frame */}
      <div
        className="origin-top-left shadow bg-white mx-auto"
        style={{
          width: paper.w,            // px
          height: paper.h,           // px
          transform: `scale(${zoom})`,
          transformOrigin: "top left",
        }}
      >
        <div className="w-full h-full overflow-hidden p-4 min-h-0 relative rn-hide-last-col">
          <EditableTable />
          <div className="absolute bottom-4 right-4 text-xs text-667085 bg-white px-2 py-1 rounded border border-EAECF0">
            Page {state.currentPage} of {state.totalPages}
          </div>
        </div>
      </div>
    </main>
  );
}
